import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Star, Trophy, Medal, Mail, Calendar, User as UserIcon, Award, Crown, Share2, Copy, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { getVid } from "@/lib/vid";
import ContactAdmin from "@/components/ContactAdmin";
import AccountSettings from "@/components/AccountSettings";
import { useAuth } from "@/context/AuthContext";

function compressImage(file, max = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [ref, setRef] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [histCount, setHistCount] = useState(10);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/challenge/me").then((r) => setStats(r.data)).catch(() => {});
    api.get("/referral/me", { params: { vid: getVid() } }).then((r) => setRef(r.data)).catch(() => {});
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(file);
      const res = await api.post("/profile/avatar", { avatar: dataUrl });
      updateUser(res.data);
      toast.success("Photo de profil mise à jour !");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'envoi");
    } finally {
      setUploading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="text-[#39FF14] font-script text-2xl">Mon espace</div>
        <h1 className="text-4xl sm:text-5xl font-black">Mon profil</h1>
      </div>

      <div className="rounded-2xl border border-[#00E676]/15 bg-[#131F17] p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="relative">
            {user.avatar ? (
              <img src={user.avatar} alt="" className="h-28 w-28 rounded-full object-cover ring-4 ring-[#00E676]/40" data-testid="profile-avatar" />
            ) : (
              <span className="h-28 w-28 rounded-full grid place-items-center bg-[#1A2B20] text-[#39FF14] text-3xl font-black ring-4 ring-[#00E676]/30">
                {(user.prenom?.[0] || "") + (user.nom?.[0] || "")}
              </span>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="avatar-upload-btn"
              className="absolute -bottom-1 -right-1 h-10 w-10 grid place-items-center rounded-full bg-[#00E676] text-[#04180a] shadow-lg hover:scale-105 transition-transform disabled:opacity-60"
            >
              {uploading ? <Loader2 className="animate-spin" size={18} /> : <Camera size={18} />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="avatar-input" />
          </div>

          <div className="text-center sm:text-left">
            <h2 className="text-2xl font-black">{user.prenom} {user.nom}</h2>
            {stats?.is_weekly_champion && (
              <div
                data-testid="champion-badge"
                className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wide text-[#04180a] bg-gradient-to-r from-[#FFD700] to-[#FFA500] shadow-[0_0_18px_rgba(255,215,0,0.4)]"
              >
                <Crown size={14} className="fill-[#04180a]" /> Champion de la semaine
              </div>
            )}
            <div className="mt-2 space-y-1 text-sm text-[#A1B5A8]">
              <div className="flex items-center gap-2 justify-center sm:justify-start"><Mail size={14} className="text-[#00E676]" /> {user.email}</div>
              <div className="flex items-center gap-2 justify-center sm:justify-start"><UserIcon size={14} className="text-[#00E676]" /> {user.sexe}</div>
              {user.date_naissance && <div className="flex items-center gap-2 justify-center sm:justify-start"><Calendar size={14} className="text-[#00E676]" /> {new Date(user.date_naissance).toLocaleDateString("fr-FR")}</div>}
            </div>
          </div>

          {stats && (
            <div className="sm:ml-auto text-center rounded-xl border border-[#39FF14]/40 bg-gradient-to-br from-[#00E676]/15 to-transparent px-6 py-4" data-testid="profile-rank">
              <div className="flex items-center gap-1 justify-center text-[#39FF14] text-xs font-bold uppercase tracking-wide"><Award size={14} /> Classement</div>
              <div className="tabular text-4xl font-black mt-1">{stats.rank ? `${stats.rank}${stats.rank === 1 ? "er" : "e"}` : "—"}</div>
              <div className="text-xs text-[#697C6F]">sur {stats.total_players || 0} joueur{(stats.total_players || 0) > 1 ? "s" : ""}</div>
            </div>
          )}
        </div>
      </div>

      <h2 className="text-lg font-bold mt-10 mb-4">Mes statistiques (challenge)</h2>
      {!stats ? (
        <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-[#00E676]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-5">
              <Medal className="text-[#00E676]" />
              <div className="tabular text-3xl font-black mt-2">{stats.plays}</div>
              <div className="text-xs text-[#A1B5A8]">Paris joués</div>
            </div>
            <div className="rounded-xl border border-[#39FF14]/30 bg-gradient-to-br from-[#00E676]/10 to-transparent p-5">
              <Star className="text-[#39FF14]" />
              <div className="tabular text-3xl font-black mt-2" data-testid="profile-points">{stats.total_points}</div>
              <div className="text-xs text-[#A1B5A8]">Points</div>
            </div>
          </div>

          {stats.history?.length > 0 && (
            <div className="mt-6">
              <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] divide-y divide-[#00E676]/5" data-testid="profile-history">
                {stats.history.slice(0, histCount).map((h, i) => (
                  <div key={`${h.date}-${h.numero}-${i}`} className="flex items-center justify-between px-5 py-3 text-sm">
                    <div>
                      <span className="text-[#697C6F]">{new Date(h.date).toLocaleDateString("fr-FR")}</span>{" "}
                      <span className="font-medium">{h.hippodrome} {h.course}</span>{" "}
                      <span className="text-[#A1B5A8]">— n°{h.numero}</span>
                    </div>
                    <span className={`tabular font-bold ${h.points > 0 ? "text-[#39FF14]" : "text-[#697C6F]"}`}>+{h.points} pts</span>
                  </div>
                ))}
              </div>
              {histCount < stats.history.length && (
                <button
                  onClick={() => setHistCount((c) => c + 10)}
                  data-testid="profile-history-more"
                  className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold text-[#39FF14] bg-[#131F17] border border-[#00E676]/20 hover:bg-[#1A2B20] transition-colors"
                >
                  <Plus size={16} /> Voir 10 de plus ({stats.history.length - histCount} restant{stats.history.length - histCount > 1 ? "s" : ""})
                </button>
              )}
            </div>
          )}
        </>
      )}

      {ref && (
        <div className="mt-8 rounded-xl border border-[#FFD700]/25 bg-gradient-to-br from-[#00E676]/8 to-transparent p-6" data-testid="profile-referral">
          <h2 className="text-lg font-black flex items-center gap-2"><Share2 className="text-[#FFD700]" size={18} /> Mon parrainage</h2>
          <p className="text-sm text-[#A1B5A8] mt-1">
            {ref.exempt
              ? "Vous avez un accès complet aux pronostics (équipe ANT)."
              : <>Parrainages validés : <b className="text-[#39FF14]">{ref.valid_count}/{ref.needed}</b>{ref.has_access ? " — accès débloqué ✅" : ""}</>}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <input readOnly value={`${window.location.origin}/login?ref=${ref.code}`} data-testid="profile-referral-link"
              className="flex-1 min-w-[220px] bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2 text-sm text-white" />
            <button onClick={async () => {
              const link = `${window.location.origin}/login?ref=${ref.code}`;
              try { if (navigator.share) await navigator.share({ title: "A Nous Turf", text: "Rejoins-moi sur A Nous Turf ! 🐴🍀", url: link }); else { await navigator.clipboard.writeText(link); toast.success("Lien copié !"); } } catch (e) { if (e && e.name !== "AbortError") console.warn("Partage impossible:", e); }
            }} data-testid="profile-referral-share" className="px-4 py-2 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14] flex items-center gap-2"><Share2 size={16} /> Partager</button>
            <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/login?ref=${ref.code}`); toast.success("Lien copié !"); }}
              data-testid="profile-referral-copy" className="px-4 py-2 rounded-lg font-semibold text-white bg-[#131F17] border border-[#00E676]/20 flex items-center gap-2"><Copy size={16} /> Copier</button>
          </div>
          {!ref.exempt && (
            <div className={`mt-2 text-xs font-bold ${ref.share_verified ? "text-[#39FF14]" : "text-[#697C6F]"}`}>
              {ref.share_verified ? "✓ Votre partage est vérifié" : "Partage non encore vérifié — validé dès qu'une personne clique sur votre lien."}
            </div>
          )}
          <div className="mt-1 text-xs text-[#A1B5A8]" data-testid="profile-referral-clicks">
            👀 <b className="text-[#39FF14]">{ref.clicks || 0}</b> clic(s) sur votre lien de parrainage
          </div>
          {ref.filleuls?.length > 0 && (
            <div className="mt-4 rounded-lg border border-[#00E676]/10 bg-[#131F17] divide-y divide-[#00E676]/5">
              {ref.filleuls.map((f, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="font-medium">{f.name}</span>
                  <span className="text-xs text-[#A1B5A8]">{f.game_days}/{ref.game_days_needed} jours · {f.shared ? "partagé ✓" : "pas partagé"} · {f.valid ? <span className="text-[#39FF14] font-bold">validé</span> : "en cours"}</span>
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-[#697C6F]">Un parrainage est validé quand le filleul participe {ref.game_days_needed} jours au Jeu et partage le site (puis votre accès est validé).</p>
        </div>
      )}

      {user.role !== "admin" && <ContactAdmin />}

      <AccountSettings />
    </div>
  );
}
